import { useLocation } from 'react-router-dom';
import { visibleNavigation, type NavCapability, type NavSection } from './navigation';
import { activeModule } from '@/modules/active';
import { MODULE_REGISTRY } from '@/modules/registry';
import { usePermissions } from '@/hooks/usePermissions';
import type { ModuleManifest } from '@/modules/manifest';
import { useTrackingAvailability } from '@/features/tracking/useTracking';

/**
 * The module the current URL belongs to and the sidebar it draws for this
 * viewer. One hook so the sidebar, the shell and the top bar cannot disagree
 * about whether there is a rail — the hamburger must not open an empty drawer.
 */
export function useActiveModule(): {
  module: ModuleManifest | null;
  sections: NavSection[];
  hasSidebar: boolean;
} {
  const { pathname } = useLocation();
  const { permissions, isPlatform } = usePermissions();
  const module = activeModule(MODULE_REGISTRY, pathname);
  // Asked only where an entry depends on it, and only of platform staff —
  // every tracking entry is platform-only, so a tenant never makes the call.
  const wantsTracking =
    isPlatform && Boolean(module?.sections.some((section) => section.items.some((item) => item.requires === 'tracking')));
  const tracking = useTrackingAvailability(wantsTracking);
  const capabilities = new Set<NavCapability>(tracking.available ? ['tracking'] : []);
  const sections = module ? visibleNavigation(module.sections, { isPlatform, permissions, capabilities }) : [];
  return { module, sections, hasSidebar: Boolean(module) && sections.length > 0 };
}
